import { AlertTriangle, X, ExternalLink, KeyRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useSetupValidation } from "@/hooks/useSetupValidation";
import { getGitHubUrl } from "@/config";
import { Link } from "react-router";

export function SetupValidationWarning() {
  const { t } = useTranslation();
  const { showWarning, validation, dismissWarning, isLoading } = useSetupValidation();

  if (isLoading || !showWarning || !validation) {
    return null;
  }

  const issues: { key: string; items: string[] }[] = [];

  if (!validation.authTriggersConfigured && validation.missingAuthTriggers.length > 0) {
    issues.push({
      key: "authTriggers",
      items: validation.missingAuthTriggers,
    });
  }

  if (!validation.cronJobsConfigured && validation.missingCronJobs.length > 0) {
    issues.push({
      key: "cronJobs",
      items: validation.missingCronJobs,
    });
  }

  if (issues.length === 0) {
    return null;
  }

  return (
    <Alert variant="destructive" className="mb-4 border-destructive/50 bg-destructive/10">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle className="flex items-center justify-between">
        <span>{t("setupValidation.title")}</span>
        <Button variant="ghost" size="icon" className="h-6 w-6 -mr-2" onClick={dismissWarning}>
          <X className="h-4 w-4" />
        </Button>
      </AlertTitle>
      <AlertDescription className="mt-2 space-y-3">
        <p className="text-sm">{t("setupValidation.description")}</p>

        <div className="space-y-2">
          {issues.map((issue) => (
            <div key={issue.key} className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" />
              <div>
                <p className="font-medium text-sm">{t(`setupValidation.issues.${issue.key}`)}</p>
                <ul className="text-xs text-muted-foreground mt-1 list-disc list-inside">
                  {issue.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button variant="outline" size="sm" className="text-xs" asChild>
            <a
              href={getGitHubUrl.blob("docs/SUPABASE_SETUP_CHECKLIST.md")}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("setupValidation.viewDocs")}
              <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
export function SecretsWarning() {
  const { t } = useTranslation();
  const { showSecretsWarning, missingRequiredSecrets, isLoading } = useSetupValidation();

  if (isLoading || !showSecretsWarning) {
    return null;
  }

  return (
    <Alert variant="destructive" className="mb-4 border-warning/50 bg-warning/10">
      <KeyRound className="h-4 w-4 text-warning" />
      <AlertTitle className="text-warning-foreground">
        {t("setupValidation.secretsWarning.title")}
      </AlertTitle>
      <AlertDescription className="mt-2 space-y-3">
        <p className="text-sm text-warning-foreground/80">
          {t("setupValidation.secretsWarning.description")}
        </p>

        <div className="flex items-start gap-2">
          <KeyRound className="h-4 w-4 mt-0.5 shrink-0 text-warning" />
          <div>
            <p className="font-medium text-sm text-warning-foreground">
              {t("setupValidation.secretsWarning.missingSecrets")}
            </p>
            <ul className="text-xs text-warning-foreground/70 mt-1 list-disc list-inside">
              {missingRequiredSecrets.map((secret) => (
                <li key={secret}>{secret}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button variant="outline" size="sm" className="text-xs" asChild>
            <Link to="/admin/setup-status">
              {t("setupValidation.secretsWarning.viewSetup")}
              <ExternalLink className="h-3 w-3 ml-1" />
            </Link>
          </Button>
          <Button variant="outline" size="sm" className="text-xs" asChild>
            <a
              href={getGitHubUrl.blob("docs/GITHUB_SECRETS_REFERENCE.md")}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("setupValidation.secretsWarning.viewDocs")}
              <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
